import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { BaseDialog, BtnCancel } from '@components/dialog';
import { BtnPreWarning, BtnPrimary } from '@components/buttons';
import { useIntl } from 'shared/intl/use-intl';
import { ImagePreview } from './image-preview';
import { ImageEditor } from './image-editor';
import type { FilePickerResult } from './index';
import { useSequentialUploads } from './use-sequential-uploads';

type Batch = ReturnType<typeof useSequentialUploads>;

export function BatchHeading({ batch, class: titleClass }: { batch: Batch; class: string }) {
  const intl = useIntl();
  const activeIndex = batch.items.findIndex((item) => item.status === 'uploading');
  return (
    <h2 class={titleClass}>
      {batch.phase === 'complete'
        ? intl('Upload complete')
        : batch.phase === 'paused'
          ? intl('Upload paused')
          : `Uploading ${Math.max(activeIndex, 0) + 1} of ${batch.items.length}`}
    </h2>
  );
}

export function BatchActions({
  batch,
  stopClass,
  doneClass,
  onStop,
  onDone,
}: {
  batch: Batch;
  stopClass: string;
  doneClass: string;
  onStop(): void;
  onDone(): void;
}) {
  const intl = useIntl();
  const successfulCount = batch.successfulResults.length;
  if (batch.phase === 'uploading' || batch.phase === 'paused') {
    return (
      <BtnPreWarning class={stopClass} onClick={onStop}>
        {successfulCount > 0 ? intl('Stop and keep uploaded files') : intl('Cancel upload')}
      </BtnPreWarning>
    );
  }
  if (batch.phase === 'complete' && successfulCount < batch.items.length) {
    return (
      <BtnPrimary class={doneClass} onClick={onDone}>
        {intl('Done')}
      </BtnPrimary>
    );
  }
}

type MultiImagePreviewModalProps = {
  files: File[] | FileList;
  onCancel: () => void;
  onComplete: (results: FilePickerResult[]) => void;
  isPublic?: boolean;
  folderId?: string;
};

export function MultiImagePreviewModal({
  files,
  onCancel,
  onComplete,
  isPublic,
  folderId,
}: MultiImagePreviewModalProps) {
  const intl = useIntl();
  const [initialFiles] = useState(() => (Array.isArray(files) ? files : Array.from(files)));
  const batch = useSequentialUploads(initialFiles, { isPublic, folderId });
  const imageRefs = useRef(new Map<string, HTMLDivElement>());
  const [previewUrls] = useState(
    () => new Map(batch.items.map((item) => [item.id, URL.createObjectURL(item.file)])),
  );
  const [selectedImageId, setSelectedImageId] = useState<string>();
  const activeIndex = batch.items.findIndex((item) => item.status === 'uploading');
  const selectedImage = batch.items.find((item) => item.id === selectedImageId);
  const successfulCount = batch.successfulResults.length;
  const isReady = batch.phase === 'ready';
  const isComplete = batch.phase === 'complete';
  const allUploaded = isComplete && successfulCount === batch.items.length;
  const finishedRef = useRef(false);

  useEffect(() => {
    if (activeIndex >= 0) {
      imageRefs.current
        .get(batch.items[activeIndex].id)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeIndex, batch.items]);

  useEffect(
    () => () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    },
    [previewUrls],
  );

  const finish = useCallback(
    (results: FilePickerResult[]) => {
      if (finishedRef.current) {
        return;
      }
      finishedRef.current = true;
      onComplete(results);
    },
    [onComplete],
  );

  const finishCancel = useCallback(async () => {
    if (finishedRef.current) {
      return;
    }
    finish(await batch.cancel());
  }, [batch.cancel, finish]);

  useEffect(() => {
    if (allUploaded) {
      finish(batch.successfulResults);
    }
  }, [allUploaded]);

  const handleImageEditorClose = useCallback(
    (editedFile?: File) => {
      if (editedFile && selectedImageId) {
        const oldUrl = previewUrls.get(selectedImageId);
        if (oldUrl) {
          URL.revokeObjectURL(oldUrl);
        }
        previewUrls.set(selectedImageId, URL.createObjectURL(editedFile));
        batch.replaceFile(selectedImageId, editedFile);
      }
      setSelectedImageId(undefined);
    },
    [batch.replaceFile, previewUrls, selectedImageId],
  );

  if (selectedImageId && selectedImage) {
    return (
      <ImageEditor
        file={selectedImage.file}
        previewUrl={previewUrls.get(selectedImage.id)!}
        onCancel={() => handleImageEditorClose()}
        onConfirm={handleImageEditorClose}
      />
    );
  }

  return (
    <BaseDialog
      onClose={
        isReady ? onCancel : isComplete ? () => finish(batch.successfulResults) : finishCancel
      }
      dismissable={batch.phase !== 'uploading'}
      blurBg={false}
      zIndex="z-[70]"
    >
      <div class="w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden rounded-lg bg-white">
        <header class="p-6 pb-4 border-b border-gray-200 flex-shrink-0">
          {isReady ? (
            <h2 class="font-semibold text-lg">Review selected images</h2>
          ) : (
            <BatchHeading batch={batch} class="font-semibold text-lg" />
          )}
          <p class="text-gray-600 text-sm mt-1">
            {isReady
              ? 'Edit an image before uploading if needed.'
              : `${successfulCount} of ${batch.items.length} files uploaded.`}
          </p>
        </header>

        <div class="flex-grow p-6 min-h-0 overflow-y-auto">
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {batch.items.map((image) => (
              <div
                ref={(el) => {
                  if (el) {
                    imageRefs.current.set(image.id, el);
                  }
                }}
                key={image.id}
              >
                <ImagePreview
                  file={image.file}
                  previewUrl={previewUrls.get(image.id)!}
                  status={image.status}
                  progress={image.progress}
                  error={image.error}
                  onEdit={() => setSelectedImageId(image.id)}
                  onRetry={() => batch.retry(image.id)}
                  onSkip={() => batch.skip(image.id)}
                  disabled={!isReady}
                />
              </div>
            ))}
          </div>
        </div>

        <footer class="p-6 pt-4 border-t border-gray-200 flex-shrink-0 flex items-center gap-3">
          {isReady && (
            <>
              <BtnPrimary class="rounded-md" onClick={() => batch.start()}>
                {intl('Upload all images')} ({batch.items.length})
              </BtnPrimary>
              <BtnCancel
                class="px-4 py-2 rounded-md border bg-white border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
                onClick={onCancel}
              >
                {intl('Cancel')}
              </BtnCancel>
            </>
          )}
          <BatchActions
            batch={batch}
            stopClass="rounded-md px-4 py-2"
            doneClass="rounded-md"
            onStop={finishCancel}
            onDone={() => finish(batch.successfulResults)}
          />
        </footer>
      </div>
    </BaseDialog>
  );
}
